import React, { useState } from 'react';
import { modalStyles as styles } from './modalStyles';
import { useAuth } from '@clerk/clerk-react';
import { formatDDMMYYYY } from '../utils/dates';
import { colors } from '../theme';
import fieldConstants from '../constants/rmaFieldConstants.json';
import RmaCustomerSummary from './RmaCustomerSummary';
import RmaCaseEmails from './RmaCaseEmails.jsx';

const API_BASE = process.env.REACT_APP_API_URL;

const ROOT_CAUSES = fieldConstants.rootCauses || [];
const RESOLUTION_TYPES = fieldConstants.resolutionTypes || [];

function ChipPicker({ options, value, onChange, required }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
      {options.map(opt => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(active ? '' : opt.value)}
            style={{
              padding: '0.35rem 0.75rem', borderRadius: 999, fontSize: '0.82rem',
              fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
              border: `1px solid ${active ? colors.primary : colors.panelBorder}`,
              background: active ? `rgba(70,187,137,0.18)` : 'rgba(255,255,255,0.04)',
              color: active ? colors.primary : colors.textMuted,
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function computeActualDays(startedAt, finalizedAt) {
  if (!startedAt || !finalizedAt) return null;
  const s = new Date(startedAt);
  const e = new Date(finalizedAt);
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return null;
  return Math.max(1, Math.round((e - s) / (1000 * 60 * 60 * 24)));
}


export default function RmaModalShipped({ rma, onClose, onSave }) {
  const [loading, setLoading] = useState(false);
  // archived → summary; outcome already saved → saved; else → outcome form
  const hasExistingOutcome = !!(rma?.outcome?.rootCause) || rma?.customerApproval?.status === 'declined';
  const [step, setStep] = useState(
    rma?.status === 'archived' ? 'summary' : (hasExistingOutcome ? 'saved' : 'outcome')
  );

  // Outcome fields
  const [rootCause, setRootCause] = useState(rma?.outcome?.rootCause || '');
  const [resolution, setResolution] = useState(rma?.outcome?.resolution || '');
  const [partsReplaced, setPartsReplaced] = useState(rma?.outcome?.partsReplaced || []);
  const [outcomeNotes, setOutcomeNotes] = useState(rma?.outcome?.notes || '');
  const [technicianTimeHours, setTechnicianTimeHours] = useState(rma?.outcome?.technicianTimeHours ?? '');
  const [componentAgeMonths, setComponentAgeMonths] = useState(rma?.outcome?.componentAgeMonths ?? '');
  const [outcomeError, setOutcomeError] = useState('');
  const [newPartName, setNewPartName] = useState('');
  const [newPartNumber, setNewPartNumber] = useState('');

  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const { getToken } = useAuth();

  const fetchAiSuggestion = async () => {
    if (aiLoading) return;
    setAiLoading(true);
    setAiSuggestion(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/knowledge/suggest`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          symptom:          rma.description || '',
          productNumber:    rma.productNumber || '',
          serialNumber:     rma.serialNumber || '',
          repairCategories: Array.isArray(rma.repairCategories) ? rma.repairCategories : [],
          assessmentNotes:  rma.assessmentNotes || '',
          firmwareVersion:  rma.firmwareVersion || '',
        }),
      });
      const data = await res.json();
      if (data.ok && data.suggestion) setAiSuggestion({ ...data.suggestion, similarCases: data.similarCases });
      else setAiSuggestion({ error: data.message || data.error || 'No suggestion available.' });
    } catch {
      setAiSuggestion({ error: 'Could not reach suggestion service.' });
    } finally {
      setAiLoading(false);
    }
  };

  const repair = rma?.repair && typeof rma.repair === 'object' ? rma.repair : {};
  const completedTasks = Array.isArray(repair.tasks) ? repair.tasks.filter(t => t?.done) : [];
  const actualDays = computeActualDays(repair.startedAt, repair.finalizedAt);
  const estimatedDays = typeof repair.timing?.repairDays === 'number' && repair.timing.repairDays > 0
    ? repair.timing.repairDays : null;
  const completedDate = repair.finalizedAt ? formatDDMMYYYY(repair.finalizedAt) : null;

  // Declined flow derived state
  const approvalStatus = rma?.customerApproval?.status;
  const isDeclined = approvalStatus === 'declined';
  const postDecline = rma?.postDecline && typeof rma.postDecline === 'object' ? rma.postDecline : {};
  const declineChoice = postDecline.choice || null;
  const returnedAsIs = declineChoice === 'return_as_is';
  const retainedAndClosed = declineChoice === 'retain_and_close';
  const paidShipping = postDecline.shippingCostAck === true;
  const declinedAt = postDecline.decidedAt ? formatDDMMYYYY(postDecline.decidedAt) : null;
  const paymentData = repair.payment && typeof repair.payment === 'object' ? repair.payment : {};
  const repairPaymentReceived = paymentData.status === 'received';
  const repairPaymentAmount = paymentData.amount || null;
  const repairPaymentCurrency = paymentData.currency || 'EUR';

  const buildOutcome = () => ({
    rootCause, resolution, partsReplaced, notes: outcomeNotes,
    technicianTimeHours: technicianTimeHours !== '' ? Number(technicianTimeHours) : null,
    componentAgeMonths:  componentAgeMonths  !== '' ? Number(componentAgeMonths)  : null,
  });

  const handleSaveOutcome = async () => {
    if (!isDeclined && !rootCause) { setOutcomeError('Select a root cause.'); return; }
    if (!isDeclined && !resolution) { setOutcomeError('Select a resolution.'); return; }
    setOutcomeError('');
    setLoading(true);
    try {
      const token = await getToken();
      await fetch(`${API_BASE}/rma/${rma._id}/outcome`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ outcome: buildOutcome() }),
      });
      setStep('saved');
    } catch (err) {
      console.error('Failed to save outcome', err);
      alert('Failed to save outcome. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveManually = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/rma/archive/${rma._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ outcome: buildOutcome() }),
      });
      const updated = await res.json();
      onSave(updated.archived || updated, { keepOpen: true });
      setStep('summary');
    } catch (err) {
      console.error('Failed to archive RMA', err);
      alert('Failed to archive. Try again.');
    } finally {
      setLoading(false);
    }
  };

  // ─── Single-step: outcome + close ───────────────────────────────────────────
  if (step === 'outcome') {
    return (
      <div style={styles.overlay}>
        <div style={{ ...styles.modal, maxWidth: 520 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <h2 style={{ margin: 0 }}>Record Outcome</h2>
          </div>
          <RmaCustomerSummary rma={rma} />
          <p style={{ color: colors.textMuted, fontSize: '0.88rem', marginBottom: '1.5rem', marginTop: 0 }}>
            Save the diagnostic outcome. The customer will confirm receipt and close the case — the case archives at that point.
            {!isDeclined && <strong style={{ color: colors.textBody }}> Root cause and resolution are required.</strong>}
          </p>

          {!isDeclined && (
            <>
              {/* AI suggestion panel */}
              <div style={{ borderRadius: 8, border: '1px solid rgba(70,187,137,0.25)', background: 'rgba(70,187,137,0.04)', padding: '0.85rem', marginBottom: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '0.8rem', fontWeight: 700, color: colors.primary, letterSpacing: '0.05em', textTransform: 'uppercase' }}>AI Outcome Suggestion</span>
                    <span style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.35)', color: '#fbbf24', borderRadius: 999, padding: '1px 7px' }}>
                      Experimental
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {aiSuggestion && !aiSuggestion.error && (
                      <button
                        type="button"
                        onClick={() => {
                          if (aiSuggestion.rootCause) setRootCause(aiSuggestion.rootCause);
                          if (aiSuggestion.resolution) setResolution(aiSuggestion.resolution);
                          if (aiSuggestion.suggestedParts?.length) {
                            setPartsReplaced(prev => {
                              const existing = prev.map(p => p.name?.toLowerCase());
                              const toAdd = aiSuggestion.suggestedParts
                                .filter(p => !existing.includes(p.toLowerCase()))
                                .map(p => ({ name: p, partNumber: '' }));
                              return [...prev, ...toAdd];
                            });
                          }
                        }}
                        style={{ padding: '0.3rem 0.8rem', borderRadius: 6, border: '1px solid rgba(70,187,137,0.4)', background: 'rgba(70,187,137,0.18)', color: colors.primary, fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}
                      >
                        Apply to form
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={fetchAiSuggestion}
                      disabled={aiLoading}
                      style={{ padding: '0.3rem 0.8rem', borderRadius: 6, border: `1px solid ${colors.primary}`, background: 'rgba(70,187,137,0.12)', color: colors.primary, fontSize: '0.78rem', fontWeight: 700, cursor: aiLoading ? 'wait' : 'pointer', opacity: aiLoading ? 0.6 : 1 }}
                    >
                      {aiLoading ? 'Analysing…' : aiSuggestion ? 'Refresh' : 'Get suggestion'}
                    </button>
                  </div>
                </div>
                {aiSuggestion && !aiSuggestion.error && (
                  <div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '0.78rem', background: 'rgba(70,187,137,0.15)', border: '1px solid rgba(70,187,137,0.4)', color: colors.primary, borderRadius: 20, padding: '2px 10px', fontWeight: 700 }}>
                        {aiSuggestion.rootCause} · {aiSuggestion.rootCauseConfidence}
                      </span>
                      <span style={{ fontSize: '0.78rem', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: colors.textBody, borderRadius: 20, padding: '2px 10px' }}>
                        → {aiSuggestion.resolution} · {aiSuggestion.resolutionConfidence}
                      </span>
                      <span style={{ fontSize: '0.78rem', borderRadius: 20, padding: '2px 10px', fontWeight: 700, border: '1px solid', ...(aiSuggestion.warrantyLikelihood === 'likely' ? { background: 'rgba(70,187,137,0.12)', borderColor: 'rgba(70,187,137,0.4)', color: colors.primary } : aiSuggestion.warrantyLikelihood === 'unlikely' ? { background: 'rgba(248,113,113,0.1)', borderColor: 'rgba(248,113,113,0.4)', color: '#f87171' } : { background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.12)', color: colors.textMuted }) }}>
                        warranty: {aiSuggestion.warrantyLikelihood}
                      </span>
                      {aiSuggestion.estimatedDurationDays && (
                        <span style={{ fontSize: '0.78rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: colors.textMuted, borderRadius: 20, padding: '2px 10px' }}>
                          ~{aiSuggestion.estimatedDurationDays}d repair
                        </span>
                      )}
                    </div>
                    {aiSuggestion.suggestedParts?.length > 0 && (
                      <p style={{ margin: '0 0 0.4rem', fontSize: '0.78rem', color: colors.textMuted }}>
                        Likely parts: <strong style={{ color: colors.textBody }}>{aiSuggestion.suggestedParts.join(', ')}</strong>
                      </p>
                    )}
                    <p style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', color: colors.textBody, lineHeight: 1.5 }}>{aiSuggestion.reasoning}</p>
                    {aiSuggestion.similarCases?.length > 0 && (
                      <p style={{ margin: 0, fontSize: '0.75rem', color: colors.textMuted }}>
                        Based on: {aiSuggestion.similarCases.map(c => `${c.rmaId} (${c.score}%)`).join(', ')}
                      </p>
                    )}
                  </div>
                )}
                {!aiSuggestion && !aiLoading && (
                  <p style={{ margin: '0 0 0.6rem', fontSize: '0.75rem', color: colors.textMuted, lineHeight: 1.5 }}>
                    Each request queries OpenAI and uses a small number of API tokens (~$0.001 per call). Use once you are ready to close — avoid refreshing repeatedly.
                  </p>
                )}
                {aiSuggestion?.error && <p style={{ margin: 0, fontSize: '0.8rem', color: colors.textMuted }}>{aiSuggestion.error}</p>}
              </div>

              {/* Root cause */}
              <div style={{ marginBottom: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <label style={{ fontWeight: 700, color: colors.textStrong, fontSize: '0.9rem' }}>Root Cause</label>
                  <span style={{ fontSize: '0.75rem', color: colors.danger, fontWeight: 700 }}>REQUIRED</span>
                </div>
                <ChipPicker options={ROOT_CAUSES} value={rootCause} onChange={setRootCause} />
              </div>

              {/* Resolution */}
              <div style={{ marginBottom: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <label style={{ fontWeight: 700, color: colors.textStrong, fontSize: '0.9rem' }}>Resolution</label>
                  <span style={{ fontSize: '0.75rem', color: colors.danger, fontWeight: 700 }}>REQUIRED</span>
                </div>
                <ChipPicker options={RESOLUTION_TYPES} value={resolution} onChange={setResolution} />
              </div>

              {/* Parts replaced */}
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ fontWeight: 700, color: colors.textStrong, fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
                  Parts Replaced <span style={{ fontWeight: 400, color: colors.textMuted }}>(optional)</span>
                </label>
                {partsReplaced.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '0.6rem' }}>
                    {partsReplaced.map((p, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.88rem', background: 'rgba(255,255,255,0.04)', borderRadius: 6, padding: '0.4rem 0.6rem', border: `1px solid ${colors.panelBorder}` }}>
                        <span style={{ flex: 1, color: colors.textBody }}>{p.name}{p.partNumber ? ` (${p.partNumber})` : ''}</span>
                        <button type="button" onClick={() => setPartsReplaced(ps => ps.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem' }}>×</button>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <input value={newPartName} onChange={e => setNewPartName(e.target.value)} placeholder="Part name" style={{ flex: '1 1 140px', padding: '0.4rem 0.6rem', borderRadius: 6, border: `1px solid ${colors.panelBorder}`, background: 'rgba(255,255,255,0.05)', color: colors.textStrong, fontSize: '0.85rem' }} />
                  <input value={newPartNumber} onChange={e => setNewPartNumber(e.target.value)} placeholder="Part number (optional)" style={{ flex: '1 1 140px', padding: '0.4rem 0.6rem', borderRadius: 6, border: `1px solid ${colors.panelBorder}`, background: 'rgba(255,255,255,0.05)', color: colors.textStrong, fontSize: '0.85rem' }} />
                  <button type="button"
                    onClick={() => { if (!newPartName.trim()) return; setPartsReplaced(ps => [...ps, { name: newPartName.trim(), partNumber: newPartNumber.trim() }]); setNewPartName(''); setNewPartNumber(''); }}
                    style={{ padding: '0.4rem 0.75rem', borderRadius: 6, border: `1px solid ${colors.panelBorder}`, background: 'rgba(255,255,255,0.07)', color: colors.textStrong, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}
                  >+ Add</button>
                </div>
              </div>
            </>
          )}

          {/* Technician time + component age — optional data quality fields */}
          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 160 }}>
              <label style={{ fontWeight: 700, color: colors.textStrong, fontSize: '0.9rem', display: 'block', marginBottom: '0.4rem' }}>
                Technician Hours <span style={{ fontWeight: 400, color: colors.textMuted }}>(optional)</span>
              </label>
              <input
                type="number" min="0" step="0.5"
                value={technicianTimeHours}
                onChange={e => setTechnicianTimeHours(e.target.value)}
                placeholder="e.g. 2.5"
                style={{ width: '100%', padding: '0.5rem 0.65rem', borderRadius: 6, border: `1px solid ${colors.panelBorder}`, background: 'rgba(255,255,255,0.05)', color: colors.textStrong, fontSize: '0.88rem', boxSizing: 'border-box' }}
              />
              <p style={{ fontSize: '0.74rem', color: colors.textMuted, margin: '0.25rem 0 0' }}>Actual hours spent on this repair.</p>
            </div>
            <div style={{ flex: 1, minWidth: 160 }}>
              <label style={{ fontWeight: 700, color: colors.textStrong, fontSize: '0.9rem', display: 'block', marginBottom: '0.4rem' }}>
                Failed Component Age <span style={{ fontWeight: 400, color: colors.textMuted }}>(optional)</span>
              </label>
              <input
                type="number" min="0"
                value={componentAgeMonths}
                onChange={e => setComponentAgeMonths(e.target.value)}
                placeholder="months, if known"
                style={{ width: '100%', padding: '0.5rem 0.65rem', borderRadius: 6, border: `1px solid ${colors.panelBorder}`, background: 'rgba(255,255,255,0.05)', color: colors.textStrong, fontSize: '0.88rem', boxSizing: 'border-box' }}
              />
              <p style={{ fontSize: '0.74rem', color: colors.textMuted, margin: '0.25rem 0 0' }}>Age of the failed component in months, if known.</p>
            </div>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ fontWeight: 700, color: colors.textStrong, fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
              Notes <span style={{ fontWeight: 400, color: colors.textMuted }}>(optional)</span>
            </label>
            <textarea
              value={outcomeNotes}
              onChange={e => setOutcomeNotes(e.target.value)}
              placeholder="Additional context about the fault or repair…"
              rows={3}
              style={{ width: '100%', padding: '0.5rem 0.65rem', borderRadius: 6, border: `1px solid ${colors.panelBorder}`, background: 'rgba(255,255,255,0.05)', color: colors.textStrong, fontSize: '0.88rem', resize: 'vertical', boxSizing: 'border-box' }}
            />
          </div>

          {/* The correspondence for this case (issue #681) */}
          <RmaCaseEmails rma={rma} />

          {outcomeError && <p style={{ color: colors.danger, fontSize: '0.88rem', marginBottom: '0.75rem' }}>{outcomeError}</p>}

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={handleSaveOutcome}
              disabled={loading}
              style={{ ...styles.button, opacity: loading ? 0.6 : 1 }}
            >
              {loading ? 'Saving…' : 'Save Outcome'}
            </button>
            <button onClick={onClose} style={styles.buttonAlt}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Saved: waiting for customer ────────────────────────────────────────────
  if (step === 'saved') {
    return (
      <div style={styles.overlay}>
        <div style={{ ...styles.modal, maxWidth: 500 }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%',
              backgroundColor: 'rgba(70,187,137,0.12)', border: '1px solid rgba(70,187,137,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0
            }}>✓</div>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.15rem', color: colors.textStrong }}>Outcome Saved</h2>
              <div style={{ fontSize: '0.82rem', color: colors.textMuted, marginTop: '0.1rem' }}>
                Awaiting customer receipt confirmation
              </div>
            </div>
          </div>

          {/* Info box */}
          <div style={{
            borderRadius: 10, border: '1px solid rgba(70,187,137,0.25)',
            background: 'rgba(70,187,137,0.05)', padding: '0.9rem 1rem', marginBottom: '1.25rem'
          }}>
            <p style={{ margin: 0, fontSize: '0.88rem', color: colors.textBody, lineHeight: 1.6 }}>
              Diagnostic data recorded. The customer will be prompted to confirm receipt and close the case —
              which is when the satisfaction survey triggers.
            </p>
          </div>

          {/* Saved outcome summary */}
          {!isDeclined && (rootCause || resolution || partsReplaced.length > 0) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem', marginBottom: '1.25rem' }}>
              {rootCause && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                  <span style={{ color: colors.textMuted }}>Root cause</span>
                  <span style={{ color: colors.textStrong, fontWeight: 600 }}>{rootCause}</span>
                </div>
              )}
              {resolution && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                  <span style={{ color: colors.textMuted }}>Resolution</span>
                  <span style={{ color: colors.textStrong, fontWeight: 600 }}>{resolution}</span>
                </div>
              )}
              {partsReplaced.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                  <span style={{ color: colors.textMuted }}>Parts replaced</span>
                  <span style={{ color: colors.textStrong, fontWeight: 600 }}>
                    {partsReplaced.map(p => p.name).join(', ')}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Manual archive fallback */}
          <div style={{
            borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: '1.1rem', marginBottom: '1.1rem'
          }}>
            <p style={{ margin: '0 0 0.65rem', fontSize: '0.8rem', color: colors.textMuted, lineHeight: 1.5 }}>
              If the customer hasn't confirmed receipt and you need to close this case, you can archive it manually.
            </p>
            <button
              onClick={handleArchiveManually}
              disabled={loading}
              style={{
                padding: '0.45rem 1rem', borderRadius: 6, fontSize: '0.82rem', fontWeight: 600,
                border: `1px solid ${colors.panelBorder}`, background: 'rgba(255,255,255,0.05)',
                color: colors.textMuted, cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.5 : 1
              }}
            >
              {loading ? 'Archiving…' : 'Archive manually'}
            </button>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button onClick={() => setStep('outcome')} style={styles.buttonAlt}>Edit outcome</button>
            <button onClick={onClose} style={{ ...styles.button, background: 'rgba(255,255,255,0.06)', color: colors.textBody }}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Summary ─────────────────────────────────────────────────────────────────
  if (step === 'summary') {
    return (
      <div style={styles.overlay}>
        <div style={{ ...styles.modal, maxWidth: '500px' }}>
          <h2 style={{ marginBottom: '0.25rem' }}>Case Archived</h2>
          <p style={{ color: colors.textMuted, marginBottom: '1.25rem', fontSize: '0.9rem' }}>
            Case closed and archived successfully.
          </p>
          <RmaCustomerSummary rma={rma} />

          {/* Service summary — declined flow */}
          {isDeclined ? (
            <div style={{
              borderRadius: '12px', border: '1px solid rgba(251,146,60,0.25)',
              backgroundColor: 'rgba(251,146,60,0.06)', padding: '1rem',
              display: 'flex', flexDirection: 'column', gap: '0.6rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{ fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.textMuted }}>
                Case Summary
              </div>

              {/* Outcome badge */}
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem', padding: '0.5rem 0.75rem', borderRadius: '8px', backgroundColor: 'rgba(251,146,60,0.12)', border: '1px solid rgba(251,146,60,0.3)' }}>
                  <span style={{ fontSize: '0.7rem', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Outcome</span>
                  <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#fb923c' }}>
                    {returnedAsIs ? 'Repair declined — unit returned' : retainedAndClosed ? 'Repair declined — unit retained' : 'Repair declined'}
                  </span>
                </div>
                {declinedAt && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem', padding: '0.5rem 0.75rem', borderRadius: '8px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <span style={{ fontSize: '0.7rem', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Decision date</span>
                    <span style={{ fontWeight: 700, fontSize: '1rem', color: colors.textStrong }}>{declinedAt}</span>
                  </div>
                )}
              </div>

              {/* Fact rows */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', marginTop: '0.15rem' }}>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.88rem' }}>
                  <span style={{ color: '#fb923c', fontWeight: 700, minWidth: '1rem' }}>–</span>
                  <span style={{ color: colors.textBody }}>Repair quote was reviewed and <strong>declined</strong> by the customer</span>
                </div>
                {returnedAsIs && (
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.88rem' }}>
                    <span style={{ color: '#fb923c', fontWeight: 700, minWidth: '1rem' }}>–</span>
                    <span style={{ color: colors.textBody }}>
                      Unit returned as-is
                      {paidShipping && <span style={{ color: colors.textMuted }}> · return shipping charged</span>}
                    </span>
                  </div>
                )}
                {retainedAndClosed && (
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.88rem' }}>
                    <span style={{ color: '#fb923c', fontWeight: 700, minWidth: '1rem' }}>–</span>
                    <span style={{ color: colors.textBody }}>Unit retained and case closed — no device returned</span>
                  </div>
                )}
                {repairPaymentReceived && (
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.88rem' }}>
                    <span style={{ color: '#fb923c', fontWeight: 700, minWidth: '1rem' }}>–</span>
                    <span style={{ color: colors.textBody }}>
                      Repair payment received
                      {repairPaymentAmount != null && <span style={{ color: colors.textMuted }}> · {repairPaymentAmount} {repairPaymentCurrency}</span>}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ) : (
          /* Service summary — normal repair flow */
          <div style={{
            borderRadius: '12px', border: '1px solid rgba(70,187,137,0.2)',
            backgroundColor: 'rgba(70,187,137,0.06)', padding: '1rem',
            display: 'flex', flexDirection: 'column', gap: '0.6rem',
            marginBottom: '1.5rem'
          }}>
            <div style={{ fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: colors.textMuted }}>
              Service Summary
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              {actualDays !== null && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem', padding: '0.5rem 0.75rem', borderRadius: '8px', backgroundColor: 'rgba(70,187,137,0.1)', border: '1px solid rgba(70,187,137,0.25)' }}>
                  <span style={{ fontSize: '0.7rem', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Service time</span>
                  <span style={{ fontWeight: 700, fontSize: '1rem', color: colors.accent }}>{actualDays} days</span>
                  {estimatedDays && <span style={{ fontSize: '0.72rem', color: colors.textDim }}>Est. {estimatedDays} days</span>}
                </div>
              )}
              {completedDate && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem', padding: '0.5rem 0.75rem', borderRadius: '8px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <span style={{ fontSize: '0.7rem', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Completed</span>
                  <span style={{ fontWeight: 700, fontSize: '1rem', color: colors.textStrong }}>{completedDate}</span>
                </div>
              )}
            </div>
            {completedTasks.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Work completed
                </div>
                {completedTasks.map((task, i) => (
                  <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', fontSize: '0.88rem' }}>
                    <span style={{ color: colors.accent, fontWeight: 700 }}>✓</span>
                    <span>
                      <span style={{ fontWeight: 600, color: colors.textStrong }}>{task.label}</span>
                      {task.note && <span style={{ display: 'block', fontSize: '0.8rem', color: colors.textDim }}>{task.note}</span>}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          )}

          <button onClick={onClose} style={{ ...styles.button, marginTop: '0.5rem' }}>Close</button>
        </div>
      </div>
    );
  }
}
